// IF (03/11/20): This logic is currently duplicated on the client and server which is a recipe for disaster
// but leaving it as-is for now until we figure out to share code between the client and server

import moment from 'moment-timezone';
import Mustache from 'mustache';

import { APPLICATION_DOMAIN } from '../../config/config';
import { formatLocation, formatCompensation, combineFullAddress } from '../../util/formatters';

const standInApplication = {
  isDefaultApplicant: true,
  firstName: '[Applicant first name]',
  lastName: '[Applicant last name]',
  email: '[Applicant email]',
  interviewStartAt: '[Applicant interview date and time]',
  interviewEndAt: null,
  startDateTime: '[Applicant start date]',
  applicationStartLink: '[Link to job application page]',
  applicationWorkHistoryLink: '[Link to work history entry page]',
  applicationExperienceLink: '[Link to application experience page]',
};

const standInJob = {
  title: '[Job title]',
  job_hash: '[unique job id]',
  officeStreet: '[Office street]',
  officeUnit: '[Office unit]',
  officeCity: '[Office city]',
  officeState: '[Office state]',
  officeZipCode: '[Office zip Code]',
  city: '[City]',
  state: '[State]',
  timeZoneId: null,
  compensationType: '[Annual or Yearly compensation]',
  compensationHigh: 100,
  compensationLow: 1,
  org: {} as Org,
};

type ApplicantMessageTemplateOptions = {
  jobApplicant?: JobApplicant | null;
  fullApplicant?: FullApplicant | null;
  useStandInApplicant?: boolean;
  useStandInJob?: boolean;
  org?: Org;
  job?: Job;
  user?: AuthenticatedUser;
  interview?: Interview | null;
  referenceDate?: moment.Moment;
  dynamicReplacements?: { [variableName: string]: string };
};

type SimpleApplicantInfo = {
  firstName: string;
  lastName: string;
  email: string | null;
  interviewStartAt: string | Date | null;
  interviewEndAt: string | Date | null;
  startDateTime: string | Date | null;
  applicationStartLink: string;
  applicationWorkHistoryLink: string;
  applicationExperienceLink: string;
};

type SimpleJobInfo = {
  title: string;
  job_hash: string;
  officeStreet: string;
  officeUnit: string;
  officeCity: string;
  officeState: string;
  officeZipCode: string;
  city: string;
  state: string;
  timeZoneId: string | null;
  compensationType: string;
  compensationHigh: number;
  compensationLow: number;
  org: Org;
};

type ApplicantContext = Omit<
  SimpleApplicantInfo,
  'interviewStartAt' | 'interviewEndAt' | 'startDateTime'
> & {
  interviewStartAt: undefined;
  interviewEndAt: undefined;
  interviewDateTime: string | Date | null;
  startDateTime: undefined;
  startDate: string | Date | null;
};
type UserContext = {
  firstName: string;
  lastName: string;
  fullName: string;
};
type JobContext = {
  title: string;
  companyName: string;
  companyWebsite: string | null;
  compensation: string | null;
  applyLink: string;
  location: string | null;
  officeAddress: string | null;
};

export class ApplicantMessageTemplateParser implements MessageTemplateParser {
  applicantInfo!: SimpleApplicantInfo;

  useStandInApplicant: boolean;

  job: Job | SimpleJobInfo;

  user: AuthenticatedUser | null;

  dynamicReplacements: { [variableName: string]: string };

  referenceDate: moment.Moment;

  context!: {
    applicant: ApplicantContext;
    user: {} | UserContext;
    job: JobContext;
    [variableName: string]: unknown;
  };

  /*
  Reference date is used for processing texts that will be sent sometime
  in the future, like interview reminders. We want relative dates
  (e.g. tomorrow, today) to be based on when the message will be sent,
  not the current date/time.
   */
  constructor({
    jobApplicant,
    fullApplicant,
    useStandInApplicant,
    useStandInJob,
    org,
    job,
    user,
    interview,
    dynamicReplacements,
    referenceDate,
  }: ApplicantMessageTemplateOptions) {
    if (!jobApplicant && !fullApplicant && !useStandInApplicant) {
      throw new Error('Must provide one of jobApplicant, fullApplicant, or useStandInApplicant');
    }
    this.useStandInApplicant = !!useStandInApplicant;
    if (jobApplicant) {
      this.applicantInfo = {
        firstName: jobApplicant.person.firstName,
        lastName: jobApplicant.person.lastName,
        email: jobApplicant.person.email,
        interviewStartAt: interview?.startAt || null,
        interviewEndAt: interview?.endAt || null,
        startDateTime: jobApplicant.hireStartDateTime || null,
        applicationStartLink: `${APPLICATION_DOMAIN}/wizard/${jobApplicant.job_application_hash}/start`,
        applicationWorkHistoryLink: `${APPLICATION_DOMAIN}/wizard/${jobApplicant.job_application_hash}/workhistory`,
        applicationExperienceLink: `${APPLICATION_DOMAIN}/wizard/${jobApplicant.job_application_hash}/experience`,
      };
    } else if (fullApplicant) {
      this.applicantInfo = {
        firstName: fullApplicant.firstName,
        lastName: fullApplicant.lastName,
        email: fullApplicant.email,
        interviewStartAt: fullApplicant.interviewStartAt || null,
        interviewEndAt: fullApplicant.interviewEndAt || null,
        startDateTime: fullApplicant.hireStartDateTime || null,
        applicationStartLink: `${APPLICATION_DOMAIN}/wizard/${fullApplicant.job_application_hash}/start`,
        applicationWorkHistoryLink: `${APPLICATION_DOMAIN}/wizard/${fullApplicant.job_application_hash}/workhistory`,
        applicationExperienceLink: `${APPLICATION_DOMAIN}/wizard/${fullApplicant.job_application_hash}/experience`,
      };
    } else if (useStandInApplicant) {
      this.applicantInfo = standInApplication;
    }

    if (job) {
      this.job = job;
    } else if (useStandInJob && org) {
      this.job = standInJob;
      this.job.org = org;
    } else {
      throw new Error('Must provide either job or both org and useStandInJob');
    }

    this.user = user || null;
    this.referenceDate = referenceDate || moment.utc();
    this.dynamicReplacements = dynamicReplacements || {};
    this.generateContext();
  }

  setReferenceDate(date: moment.Moment) {
    this.referenceDate = date;
    this.generateContext();
  }

  generateContext() {
    this.context = {
      applicant: {
        ...this.applicantInfo,
        interviewStartAt: undefined,
        interviewEndAt: undefined,
        interviewDateTime: this.formatInterviewDateTime(),
        startDateTime: undefined,
        startDate: this.formatStartDateTime(),
      },
      user: this.user
        ? {
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            fullName: `${this.user.firstName} ${this.user.lastName}`,
          }
        : {},
      job: {
        title: this.job.title,
        companyName: this.job.org.name,
        companyWebsite: this.job.org.website,
        compensation: formatCompensation(this.job),
        applyLink: `${APPLICATION_DOMAIN}/apply/${this.job.job_hash}`,
        location: formatLocation(this.job),
        officeAddress: combineFullAddress(
          this.job.officeStreet,
          this.job.officeUnit,
          this.job.officeCity,
          this.job.officeState,
          this.job.officeZipCode,
        ),
      },
      ...this.dynamicReplacements,
    };
  }

  formatInterviewDateTime() {
    if (this.useStandInApplicant) {
      return this.applicantInfo.interviewStartAt;
    }

    const interviewTimes = {
      startAt: this.applicantInfo.interviewStartAt,
      endAt: this.applicantInfo.interviewEndAt,
    };

    if (!interviewTimes.startAt) {
      return '';
    }

    let interviewMoment = moment.utc(interviewTimes.startAt);
    if (this.job?.timeZoneId) {
      interviewMoment = interviewMoment.tz(this.job.timeZoneId);
    }
    let interviewDate = interviewMoment.format('dddd, MMMM Do');
    const startTime = interviewMoment.format('h:mm a');

    if (interviewMoment.isSame(this.referenceDate, 'day')) {
      interviewDate = 'today';
    } else if (interviewMoment.subtract(1, 'day').isSame(this.referenceDate, 'day')) {
      interviewDate = 'tomorrow';
    }

    if (interviewTimes.endAt) {
      let endTimeMoment = moment.utc(interviewTimes.endAt);
      if (this.job?.timeZoneId) {
        endTimeMoment = endTimeMoment.tz(this.job.timeZoneId);
      }
      return `${interviewDate} between ${startTime} and ${endTimeMoment.format('h:mm a')}`;
    }

    return `${interviewDate} at ${startTime}`;
  }

  formatStartDateTime() {
    if (this.useStandInApplicant) {
      return this.applicantInfo.startDateTime;
    }

    const { startDateTime } = this.applicantInfo;
    if (!startDateTime) {
      return '';
    }

    let startDateMoment = moment.utc(startDateTime);
    if (this.job?.timeZoneId) {
      startDateMoment = startDateMoment.tz(this.job.timeZoneId);
    }
    let date = startDateMoment.format('dddd, MMMM Do');

    if (startDateMoment.isSame(this.referenceDate, 'day')) {
      date = 'today';
    } else if (startDateMoment.subtract(1, 'day').isSame(this.referenceDate, 'day')) {
      date = 'tomorrow';
    }

    return date;
  }

  parse(template: string, isConfirmationRequired?: boolean) {
    const parsedTemplate = Mustache.render(template, this.context);
    if (isConfirmationRequired) {
      return `${parsedTemplate} Please reply YES to confirm, NO to cancel.`;
    }
    return parsedTemplate;
  }
}
